import { useCallback, useEffect, useState } from 'react';
import { getWeather, type WeatherPayload } from '../services/meteoApi';
import { eventBus } from '../services/EventBus';

const REFRESH_MS = 10 * 60 * 1000;

type Listener = () => void;

let sharedData: WeatherPayload | null = null;
let sharedLoading = false;
let sharedError: string | null = null;
let inFlight: Promise<void> | null = null;
let intervalId: number | null = null;
let subscriberCount = 0;
const listeners = new Set<Listener>();

function notify() {
  listeners.forEach((l) => l());
}

async function fetchWeather(force = false) {
  if (inFlight && !force) return inFlight;
  sharedLoading = true;
  notify();
  const run = (async () => {
    try {
      sharedData = await getWeather({ forecastDays: 16, force });
      sharedError = null;
    } catch (e: any) {
      sharedError = e?.message || 'error';
    } finally {
      sharedLoading = false;
      inFlight = null;
      notify();
    }
  })();
  inFlight = run;
  return run;
}

function ensurePolling() {
  if (intervalId != null) return;
  void fetchWeather(false);
  intervalId = window.setInterval(() => {
    void fetchWeather(false);
  }, REFRESH_MS);
  eventBus.on('meteo-location-changed', onLocationChanged);
}

function onLocationChanged() {
  void fetchWeather(true);
}

function stopPollingIfIdle() {
  if (subscriberCount > 0) return;
  if (intervalId != null) {
    window.clearInterval(intervalId);
    intervalId = null;
  }
  eventBus.off('meteo-location-changed', onLocationChanged);
}

export function useWeather(opts?: { enabled?: boolean }) {
  const enabled = opts?.enabled !== false;
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!enabled) return undefined;
    const listener = () => setTick((n) => n + 1);
    listeners.add(listener);
    subscriberCount += 1;
    ensurePolling();
    return () => {
      listeners.delete(listener);
      subscriberCount = Math.max(0, subscriberCount - 1);
      stopPollingIfIdle();
    };
  }, [enabled]);

  const refresh = useCallback(async (force = false) => {
    await fetchWeather(force);
  }, []);

  if (!enabled) {
    return { data: null, loading: false, error: null, refresh };
  }

  return {
    data: sharedData,
    loading: sharedLoading,
    error: sharedError,
    refresh,
  };
}
